'use client';
import React from 'react';

interface MedicalVisualProps {
  concern: string;
  className?: string;
}

// Subtle, premium SVG medical visuals — no emoji, no clip art
// These are abstract/anatomical illustrations with soft depth

function HeartVisual() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      <defs>
        <radialGradient id="heartGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#E8C97A" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#C9A84C" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="heartFill" cx="40%" cy="35%" r="60%">
          <stop offset="0%" stopColor="#E8C97A" stopOpacity="0.9" />
          <stop offset="60%" stopColor="#C9A84C" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#8B6914" stopOpacity="0.5" />
        </radialGradient>
        <filter id="heartShadow">
          <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#C9A84C" floodOpacity="0.25" />
        </filter>
      </defs>
      {/* Glow background */}
      <circle cx="100" cy="100" r="80" fill="url(#heartGlow)" />
      {/* Heart shape */}
      <path
        d="M100 155 C100 155 45 120 45 80 C45 60 60 48 75 48 C85 48 95 55 100 63 C105 55 115 48 125 48 C140 48 155 60 155 80 C155 120 100 155 100 155Z"
        fill="url(#heartFill)"
        filter="url(#heartShadow)"
        opacity="0.85"
      />
      {/* Highlight */}
      <path
        d="M80 65 C80 65 72 72 72 82"
        stroke="rgba(255,255,255,0.4)"
        strokeWidth="3"
        strokeLinecap="round"
      />
      {/* ECG line */}
      <path
        d="M30 110 L55 110 L65 90 L75 130 L85 100 L95 110 L105 110 L115 110 L125 95 L135 125 L145 110 L170 110"
        stroke="#C9A84C"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.5"
        fill="none"
      />
    </svg>
  );
}

function BrainVisual() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      <defs>
        <radialGradient id="brainGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#818CF8" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#6366F1" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="brainFill" cx="40%" cy="30%" r="60%">
          <stop offset="0%" stopColor="#A5B4FC" stopOpacity="0.9" />
          <stop offset="60%" stopColor="#818CF8" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#4338CA" stopOpacity="0.5" />
        </radialGradient>
        <filter id="brainShadow">
          <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#818CF8" floodOpacity="0.3" />
        </filter>
      </defs>
      <circle cx="100" cy="100" r="80" fill="url(#brainGlow)" />
      {/* Brain left hemisphere */}
      <path
        d="M100 60 C80 55 55 65 50 85 C45 105 55 125 65 135 C75 145 90 148 100 145"
        stroke="url(#brainFill)"
        strokeWidth="18"
        strokeLinecap="round"
        fill="none"
        filter="url(#brainShadow)"
        opacity="0.8"
      />
      {/* Brain right hemisphere */}
      <path
        d="M100 60 C120 55 145 65 150 85 C155 105 145 125 135 135 C125 145 110 148 100 145"
        stroke="url(#brainFill)"
        strokeWidth="18"
        strokeLinecap="round"
        fill="none"
        opacity="0.7"
      />
      {/* Folds */}
      <path d="M70 80 C75 75 80 78 78 85" stroke="rgba(165,180,252,0.4)" strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M60 100 C68 95 72 100 68 108" stroke="rgba(165,180,252,0.4)" strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M130 80 C125 75 120 78 122 85" stroke="rgba(165,180,252,0.4)" strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M140 100 C132 95 128 100 132 108" stroke="rgba(165,180,252,0.4)" strokeWidth="2" strokeLinecap="round" fill="none" />
      {/* Center line */}
      <line x1="100" y1="62" x2="100" y2="143" stroke="rgba(165,180,252,0.3)" strokeWidth="1.5" strokeDasharray="4 3" />
    </svg>
  );
}

function JointVisual() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      <defs>
        <radialGradient id="jointGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#94A3B8" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#64748B" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="boneFill" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#E2E8F0" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#94A3B8" stopOpacity="0.6" />
        </linearGradient>
        <filter id="boneShadow">
          <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#94A3B8" floodOpacity="0.3" />
        </filter>
      </defs>
      <circle cx="100" cy="100" r="80" fill="url(#jointGlow)" />
      {/* Upper bone */}
      <rect x="88" y="40" width="24" height="55" rx="12" fill="url(#boneFill)" filter="url(#boneShadow)" />
      <ellipse cx="100" cy="40" rx="16" ry="10" fill="url(#boneFill)" />
      {/* Joint cartilage */}
      <ellipse cx="100" cy="100" rx="22" ry="8" fill="rgba(201,168,76,0.3)" stroke="rgba(201,168,76,0.5)" strokeWidth="1" />
      {/* Lower bone */}
      <rect x="88" y="105" width="24" height="55" rx="12" fill="url(#boneFill)" filter="url(#boneShadow)" opacity="0.9" />
      <ellipse cx="100" cy="160" rx="16" ry="10" fill="url(#boneFill)" opacity="0.9" />
      {/* Highlight */}
      <path d="M92 50 C90 55 90 65 92 70" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function WomensHealthVisual() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      <defs>
        <radialGradient id="womensGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#F9A8D4" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#EC4899" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="womensFill" cx="40%" cy="30%" r="60%">
          <stop offset="0%" stopColor="#FBD5E8" stopOpacity="0.9" />
          <stop offset="60%" stopColor="#F472B6" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#BE185D" stopOpacity="0.5" />
        </radialGradient>
        <filter id="womensShadow">
          <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#F472B6" floodOpacity="0.25" />
        </filter>
      </defs>
      <circle cx="100" cy="100" r="80" fill="url(#womensGlow)" />
      {/* Abstract flower/lotus form */}
      <ellipse cx="100" cy="90" rx="20" ry="30" fill="url(#womensFill)" filter="url(#womensShadow)" opacity="0.8" />
      <ellipse cx="100" cy="90" rx="20" ry="30" fill="url(#womensFill)" transform="rotate(60 100 90)" opacity="0.6" />
      <ellipse cx="100" cy="90" rx="20" ry="30" fill="url(#womensFill)" transform="rotate(-60 100 90)" opacity="0.6" />
      {/* Center */}
      <circle cx="100" cy="90" r="10" fill="rgba(251,213,232,0.9)" />
      {/* Stem */}
      <line x1="100" y1="120" x2="100" y2="155" stroke="rgba(244,114,182,0.5)" strokeWidth="3" strokeLinecap="round" />
      <path d="M100 140 C110 135 118 138 115 145" stroke="rgba(244,114,182,0.4)" strokeWidth="2" strokeLinecap="round" fill="none" />
    </svg>
  );
}

function ChildHealthVisual() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      <defs>
        <radialGradient id="childGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#86EFAC" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#22C55E" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="childFill" cx="40%" cy="30%" r="60%">
          <stop offset="0%" stopColor="#BBF7D0" stopOpacity="0.9" />
          <stop offset="60%" stopColor="#4ADE80" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#15803D" stopOpacity="0.5" />
        </radialGradient>
        <filter id="childShadow">
          <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#4ADE80" floodOpacity="0.25" />
        </filter>
      </defs>
      <circle cx="100" cy="100" r="80" fill="url(#childGlow)" />
      {/* Abstract star/care symbol */}
      <path
        d="M100 45 L110 80 L148 80 L118 102 L128 137 L100 115 L72 137 L82 102 L52 80 L90 80 Z"
        fill="url(#childFill)"
        filter="url(#childShadow)"
        opacity="0.75"
      />
      {/* Inner highlight */}
      <path
        d="M100 60 L107 82 L130 82 L112 95 L119 117 L100 104 L81 117 L88 95 L70 82 L93 82 Z"
        fill="rgba(255,255,255,0.15)"
      />
    </svg>
  );
}

function DiabetesVisual() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      <defs>
        <radialGradient id="diabetesGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FCD34D" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="diabetesFill" cx="40%" cy="30%" r="60%">
          <stop offset="0%" stopColor="#FDE68A" stopOpacity="0.9" />
          <stop offset="60%" stopColor="#FBBF24" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#B45309" stopOpacity="0.5" />
        </radialGradient>
        <filter id="diabetesShadow">
          <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#FBBF24" floodOpacity="0.3" />
        </filter>
      </defs>
      <circle cx="100" cy="100" r="80" fill="url(#diabetesGlow)" />
      {/* Hexagon (metabolic/molecular) */}
      <path
        d="M100 50 L135 70 L135 110 L100 130 L65 110 L65 70 Z"
        fill="url(#diabetesFill)"
        filter="url(#diabetesShadow)"
        opacity="0.8"
      />
      {/* Inner hexagon */}
      <path
        d="M100 65 L122 77 L122 103 L100 115 L78 103 L78 77 Z"
        fill="rgba(255,255,255,0.12)"
        stroke="rgba(253,230,138,0.4)"
        strokeWidth="1"
      />
      {/* Glucose drop */}
      <path d="M100 75 C100 75 88 90 88 98 C88 105 93 110 100 110 C107 110 112 105 112 98 C112 90 100 75 100 75Z"
        fill="rgba(253,230,138,0.6)"
      />
    </svg>
  );
}

function WellnessVisual() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      <defs>
        <radialGradient id="wellnessGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#C9A84C" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#C9A84C" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="wellnessFill" cx="40%" cy="30%" r="60%">
          <stop offset="0%" stopColor="#E8C97A" stopOpacity="0.9" />
          <stop offset="60%" stopColor="#C9A84C" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#8B6914" stopOpacity="0.5" />
        </radialGradient>
        <filter id="wellnessShadow">
          <feDropShadow dx="0" dy="4" stdDeviation="10" floodColor="#C9A84C" floodOpacity="0.3" />
        </filter>
      </defs>
      <circle cx="100" cy="100" r="80" fill="url(#wellnessGlow)" />
      {/* Abstract human form */}
      <circle cx="100" cy="68" r="18" fill="url(#wellnessFill)" filter="url(#wellnessShadow)" opacity="0.85" />
      <path
        d="M72 100 C72 85 82 78 100 78 C118 78 128 85 128 100 L128 130 C128 135 124 138 120 138 L80 138 C76 138 72 135 72 130 Z"
        fill="url(#wellnessFill)"
        opacity="0.75"
      />
      {/* Arms */}
      <path d="M72 100 L50 115" stroke="url(#wellnessFill)" strokeWidth="12" strokeLinecap="round" opacity="0.7" />
      <path d="M128 100 L150 115" stroke="url(#wellnessFill)" strokeWidth="12" strokeLinecap="round" opacity="0.7" />
      {/* Legs */}
      <path d="M88 138 L82 165" stroke="url(#wellnessFill)" strokeWidth="12" strokeLinecap="round" opacity="0.7" />
      <path d="M112 138 L118 165" stroke="url(#wellnessFill)" strokeWidth="12" strokeLinecap="round" opacity="0.7" />
      {/* Highlight on head */}
      <path d="M93 62 C91 65 91 70 93 73" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function DoctorVisual() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      <defs>
        <radialGradient id="doctorGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#C9A84C" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#C9A84C" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="doctorFill" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#E8C97A" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#C9A84C" stopOpacity="0.6" />
        </linearGradient>
      </defs>
      <circle cx="100" cy="100" r="80" fill="url(#doctorGlow)" />
      {/* Stethoscope */}
      <circle cx="100" cy="75" r="22" stroke="url(#doctorFill)" strokeWidth="6" fill="none" />
      <path d="M100 97 L100 125" stroke="url(#doctorFill)" strokeWidth="6" strokeLinecap="round" />
      <path d="M100 125 C100 125 80 130 80 145 C80 155 88 160 100 160 C112 160 120 155 120 145 C120 130 100 125 100 125Z"
        stroke="url(#doctorFill)" strokeWidth="5" fill="none" strokeLinecap="round" />
      <circle cx="100" cy="160" r="6" fill="url(#doctorFill)" />
      {/* Earpieces */}
      <path d="M78 75 L60 65" stroke="url(#doctorFill)" strokeWidth="5" strokeLinecap="round" />
      <path d="M122 75 L140 65" stroke="url(#doctorFill)" strokeWidth="5" strokeLinecap="round" />
      <circle cx="58" cy="63" r="5" fill="url(#doctorFill)" />
      <circle cx="142" cy="63" r="5" fill="url(#doctorFill)" />
    </svg>
  );
}

function CheckupVisual() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      <defs>
        <radialGradient id="checkupGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#34D399" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="checkupFill" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#6EE7B7" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#10B981" stopOpacity="0.6" />
        </linearGradient>
      </defs>
      <circle cx="100" cy="100" r="80" fill="url(#checkupGlow)" />
      {/* Clipboard */}
      <rect x="60" y="55" width="80" height="100" rx="8" fill="rgba(16,185,129,0.15)" stroke="url(#checkupFill)" strokeWidth="2" />
      <rect x="80" y="48" width="40" height="16" rx="8" fill="rgba(16,185,129,0.3)" stroke="url(#checkupFill)" strokeWidth="1.5" />
      {/* Check lines */}
      <path d="M75 80 L90 80" stroke="url(#checkupFill)" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M75 95 L115 95" stroke="url(#checkupFill)" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M75 110 L110 110" stroke="url(#checkupFill)" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M75 125 L105 125" stroke="url(#checkupFill)" strokeWidth="2.5" strokeLinecap="round" />
      {/* Checkmark */}
      <path d="M95 78 L100 83 L110 73" stroke="#34D399" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FollowUpVisual() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      <defs>
        <radialGradient id="followGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#C9A84C" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#C9A84C" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="followFill" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#E8C97A" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#C9A84C" stopOpacity="0.6" />
        </linearGradient>
      </defs>
      <circle cx="100" cy="100" r="80" fill="url(#followGlow)" />
      {/* Calendar */}
      <rect x="55" y="60" width="90" height="85" rx="8" fill="rgba(201,168,76,0.1)" stroke="url(#followFill)" strokeWidth="2" />
      <rect x="55" y="60" width="90" height="22" rx="8" fill="rgba(201,168,76,0.25)" />
      {/* Calendar dots */}
      {[0,1,2,3,4,5].map((i) => (
        <circle key={i} cx={72 + (i % 3) * 28} cy={100 + Math.floor(i / 3) * 25} r="5" fill="rgba(201,168,76,0.5)" />
      ))}
      <circle cx="100" cy="100" r="5" fill="#C9A84C" />
      {/* Arrow */}
      <path d="M130 75 C140 75 145 85 140 92" stroke="url(#followFill)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <path d="M136 88 L140 92 L144 88" stroke="url(#followFill)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DefaultVisual() {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
      <defs>
        <radialGradient id="defaultGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#C9A84C" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#C9A84C" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="100" cy="100" r="80" fill="url(#defaultGlow)" />
      {/* Cross / medical symbol */}
      <rect x="85" y="55" width="30" height="90" rx="8" fill="rgba(201,168,76,0.3)" stroke="rgba(201,168,76,0.6)" strokeWidth="1.5" />
      <rect x="55" y="85" width="90" height="30" rx="8" fill="rgba(201,168,76,0.3)" stroke="rgba(201,168,76,0.6)" strokeWidth="1.5" />
    </svg>
  );
}

const visualMap: Record<string, React.FC> = {
  'heart-chest': HeartVisual,
  'brain-nervous': BrainVisual,
  'bones-joints': JointVisual,
  'womens-health': WomensHealthVisual,
  'child-health': ChildHealthVisual,
  'diabetes': DiabetesVisual,
  'fever-infection': WellnessVisual,
  'other': WellnessVisual,
  'cancer': WellnessVisual,
  'breathing': BrainVisual,
  // Primary need visuals
  'health-concern': WellnessVisual,
  'find-doctor': DoctorVisual,
  'health-checkup': CheckupVisual,
  'follow-up': FollowUpVisual,
  'specific-department': DoctorVisual,
  'default': DefaultVisual,
};

export default function MedicalVisual({ concern, className = '' }: MedicalVisualProps) {
  const Visual = visualMap[concern] ?? visualMap['default'];
  return (
    <div
      className={`medical-visual-container ${className}`}
      role="img"
      aria-label={`Medical illustration for ${concern}`}
    >
      <Visual />
    </div>
  );
}
